import { memo, useCallback, useMemo, type FC, type ReactNode } from "react";
import { Check, TriangleAlert } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getAudienceListDisplayName, type AudienceListOption } from "src/utils/audienceListLabels";
import { formatNumber } from "src/utils/numberFormat";
import CampaignStepCard from "../../../../shared/CampaignStepCard";
import {
  CAMPAIGN_TYPE_OPTIONS,
  CUSTOM_OPTION,
  MAX_PRODUCT_CATEGORIES,
  SUB_CAMPAIGN_OPTIONS,
  getToneLabelKey,
} from "../AiCampaignConstants";
import { formatCategoryName, getEmailStepValidationError } from "../AiCampaignUtils";
import type {
  AiCampaignForm,
  AiCampaignStepKey,
  CampaignSenderDetails,
  CampaignTypeValue,
} from "../AiCampaignTypes";

const KEY = "campaignFlow.aiWizard.review";
const NOT_SET = "—";

const joinListNames = (names: string[]) =>
  names.map(name => getAudienceListDisplayName(name)).join(", ");

interface ReviewSection {
  id: string;
  stepKeys: AiCampaignStepKey[];
  stepLabel: string;
  editStep: AiCampaignStepKey;
  rows: { label: string; value: ReactNode; wide?: boolean }[];
}

interface ReviewDetailsStepProps {
  form: AiCampaignForm;
  sendTo: string[];
  excludeList: string[];
  audienceOptions: AudienceListOption[];
  senderDetails: CampaignSenderDetails;
  onEdit: (_stepKey: AiCampaignStepKey) => void;
}

const ReviewDetailsStep: FC<ReviewDetailsStepProps> = ({
  form,
  sendTo,
  excludeList,
  audienceOptions,
  senderDetails,
  onEdit,
}) => {
  const { t, i18n } = useTranslation();
  const formatCount = useCallback(
    (value: number) => formatNumber(value, {}, i18n.language),
    [i18n.language]
  );

  const sections = useMemo<ReviewSection[]>(() => {
    const recipientTotal = audienceOptions
      .filter(option => sendTo.includes(option.name))
      .reduce((total, option) => total + (option.customerCount ?? 0), 0);
    const hasRecipientTotal =
      sendTo.length > 0 &&
      audienceOptions
        .filter(option => sendTo.includes(option.name))
        .every(option => option.customerCount !== undefined);

    const isCustomCategory = form.campaignType === CUSTOM_OPTION;
    const categoryOption = CAMPAIGN_TYPE_OPTIONS.find(option => option.value === form.campaignType);
    const subCategoryOption =
      form.campaignType && !isCustomCategory
        ? SUB_CAMPAIGN_OPTIONS[form.campaignType as CampaignTypeValue].find(
            option => option.value === form.subCampaign
          )
        : undefined;
    const customLabel = (name: string) => t(`${KEY}.customValue`, { name: name.trim() });
    const recipientsLabel = hasRecipientTotal
      ? t(`${KEY}.recipientsCount`, { count: formatCount(recipientTotal) })
      : "";
    const sendToValue = [joinListNames(sendTo), recipientsLabel].filter(Boolean).join(" · ");

    let categoryLabel: string = form.campaignType ?? NOT_SET;
    if (isCustomCategory) {
      categoryLabel = customLabel(form.customCampaignType);
    } else if (categoryOption) {
      categoryLabel = t(categoryOption.titleKey);
    }

    let subCategoryLabel: string = form.subCampaign ?? NOT_SET;
    if (isCustomCategory || form.subCampaign === CUSTOM_OPTION) {
      subCategoryLabel = customLabel(form.customSubCampaign);
    } else if (subCategoryOption) {
      subCategoryLabel = t(subCategoryOption.titleKey);
    }

    return [
      {
        id: "campaignDetails",
        stepKeys: ["campaignDetails"],
        stepLabel: "1",
        editStep: "campaignDetails",
        rows: [
          { label: t(`${KEY}.channel`), value: t("campaignFlow.createCampaignNew.channelEmail") },
          { label: t(`${KEY}.campaignName`), value: form.campaignName },
          { label: t(`${KEY}.sendTo`), value: sendToValue },
          {
            label: t(`${KEY}.dontSendTo`),
            value: excludeList.length ? joinListNames(excludeList) : t(`${KEY}.none`),
          },
        ],
      },
      {
        id: "products",
        stepKeys: ["productCategories"],
        stepLabel: "2",
        editStep: "productCategories",
        rows: [
          {
            label: t(`${KEY}.categories`, {
              count: formatCount(form.selectedCategories.length),
              max: formatCount(MAX_PRODUCT_CATEGORIES),
            }),
            value: (
              <ul className="review-details__chips">
                {form.selectedCategories.map(category => (
                  <li key={category} className="review-details__chip">
                    {formatCategoryName(category)}
                  </li>
                ))}
              </ul>
            ),
          },
        ],
      },
      {
        id: "campaignType",
        stepKeys: ["campaignCategory", "campaignSubCategory"],
        stepLabel: "3–4",
        editStep: "campaignCategory",
        rows: [
          { label: t(`${KEY}.category`), value: categoryLabel },
          { label: t(`${KEY}.subCategory`), value: subCategoryLabel },
        ],
      },
      {
        id: "aiPrompt",
        stepKeys: ["aiPrompt"],
        stepLabel: "5",
        editStep: "aiPrompt",
        rows: [
          {
            label: t(`${KEY}.goal`),
            value: <blockquote className="review-details__quote">“{form.prompt.trim()}”</blockquote>,
            wide: true,
          },
          { label: t(`${KEY}.tone`), value: form.tone ? t(getToneLabelKey(form.tone)) : NOT_SET },
          { label: t(`${KEY}.persona`), value: form.persona },
          { label: t(`${KEY}.seasonalTag`), value: form.seasonalTag },
          { label: t(`${KEY}.offer`), value: form.offer.trim() || NOT_SET },
          { label: t(`${KEY}.cta`), value: `${form.ctaLabel} → ${form.ctaUrl}` },
        ],
      },
      {
        id: "senderSubject",
        stepKeys: ["senderRecipients"],
        stepLabel: "6",
        editStep: "senderRecipients",
        rows: [
          { label: t(`${KEY}.subjectLine`), value: form.subjectLine },
          {
            label: t(`${KEY}.from`),
            value: `${senderDetails.fromName} <${senderDetails.fromEmail}>`,
          },
          { label: t(`${KEY}.replyTo`), value: senderDetails.replyTo || NOT_SET },
        ],
      },
    ];
  }, [audienceOptions, excludeList, form, formatCount, senderDetails, sendTo, t]);

  const validationContext = useMemo(
    () => ({ sendTo, replyTo: senderDetails.replyTo }),
    [sendTo, senderDetails.replyTo]
  );
  const isSectionComplete = (section: ReviewSection) =>
    section.stepKeys.every(key => !getEmailStepValidationError(key, form, validationContext));
  const completeCount = sections.filter(isSectionComplete).length;
  const isReady = completeCount === sections.length;
  const BannerTag = isReady ? "output" : "p";

  return (
    <CampaignStepCard title={t(`${KEY}.title`)} subtitle={t(`${KEY}.subtitle`)}>
      <BannerTag
        className={`review-details__banner ${
          isReady ? "review-details__banner--ready" : "review-details__banner--warning"
        }`}
        role={isReady ? undefined : "alert"}
      >
        {isReady ? <Check size={14} aria-hidden /> : <TriangleAlert size={14} aria-hidden />}
        <span>
          {isReady
            ? t(`${KEY}.ready`, { count: formatCount(sections.length) })
            : t(`${KEY}.incomplete`, {
                done: formatCount(completeCount),
                total: formatCount(sections.length),
              })}
        </span>
      </BannerTag>

      <div className="review-details__sections">
        {sections.map(section => {
          const isComplete = isSectionComplete(section);
          return (
            <section key={section.id} className="review-details__section">
              <header className="review-details__section-header">
                <span
                  className={`review-details__status ${
                    isComplete ? "" : "review-details__status--warning"
                  }`}
                  aria-hidden
                >
                  {isComplete ? <Check size={12} /> : <TriangleAlert size={12} />}
                </span>
                <h3 className="review-details__section-title">{t(`${KEY}.sections.${section.id}`)}</h3>
                <span className="review-details__step">
                  {t(section.stepKeys.length > 1 ? `${KEY}.steps` : `${KEY}.step`, {
                    step: section.stepLabel,
                  })}
                </span>
                <button
                  type="button"
                  className="review-details__edit"
                  onClick={() => onEdit(section.editStep)}
                >
                  {t(`${KEY}.edit`)}
                </button>
              </header>
              <dl className="review-details__rows">
                {section.rows.map(row => (
                  <div
                    key={row.label}
                    className={`review-details__row ${row.wide ? "review-details__row--wide" : ""}`}
                  >
                    <dt className="review-details__label">{row.label}</dt>
                    <dd className="review-details__value">{row.value}</dd>
                  </div>
                ))}
              </dl>
            </section>
          );
        })}
      </div>
    </CampaignStepCard>
  );
};

export default memo(ReviewDetailsStep);
